'use client';
import { track as vercelTrack } from '@vercel/analytics';
import type React from 'react';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo } from 'react';

type TrackFunction = (event: string, data?: Record<string, unknown>) => void;

/**
 * Associates the visitor with a user, or clears that association when `userId` is null — the
 * logout case, kept as one function so a caller cannot identify without being able to undo it.
 */
type IdentifyFunction = (userId: string | null, traits?: Record<string, unknown>) => void;

type AnalyticsContext = {
    track: TrackFunction;
    /**
     * Trackers contributed by this provider and its ancestors. A nested provider replaces the
     * context entirely, so without passing them down an app-level tracker (e.g. PostHog) would
     * silently stop receiving events inside any subtree that mounts its own provider.
     */
    trackers: TrackFunction[];
    /**
     * Identity is the same "one call, N destinations" shape as {@link track}, so it lives here
     * rather than beside whichever SDK happens to support it. There is no built-in counterpart to
     * `vercelTrack`: Vercel Analytics is deliberately anonymous and has no identify API, so this
     * list is empty unless an app contributes one.
     */
    identify: IdentifyFunction;
    identifiers: IdentifyFunction[];
};
const AnalyticsContext = createContext<AnalyticsContext | null>(null);

const AnalyticsProvider: React.FC<
    PropsWithChildren<{
        overallData?: Record<string, string>;
        eventPrefix?: string;
        trackers?: TrackFunction[];
        identifiers?: IdentifyFunction[];
    }>
> = ({ children, overallData = {}, eventPrefix, trackers = [], identifiers = [] }) => {
    const parentContext = useContext(AnalyticsContext);
    const parentTrackers = parentContext?.trackers;
    const parentIdentifiers = parentContext?.identifiers;

    // Inherited first, own last — order only matters for tracker side effects, not for delivery.
    const inheritedTrackers = useMemo(() => [...(parentTrackers ?? []), ...trackers], [parentTrackers, trackers]);
    const inheritedIdentifiers = useMemo(
        () => [...(parentIdentifiers ?? []), ...identifiers],
        [parentIdentifiers, identifiers]
    );

    const allTrackers = useMemo<TrackFunction[]>(
        () => [(event, data) => vercelTrack(event, data as Record<string, string>), ...inheritedTrackers],
        [inheritedTrackers]
    );

    const track = useCallback(
        (event: string, data: Record<string, unknown> = {}) => {
            const prefixedEvent = eventPrefix ? `${eventPrefix}: ${event}` : event;
            // `undefined` is dropped rather than forwarded: Vercel rejects it outright, and a
            // caller passing `cinemaId: showing.cinemaId` means "omit when unknown", not a
            // property whose value is the string "undefined" showing up as its own segment.
            const mergedData: Record<string, unknown> = { ...overallData };
            for (const [key, value] of Object.entries(data)) {
                if (value !== undefined) {
                    mergedData[key] = value;
                }
            }

            for (const t of allTrackers) {
                t(prefixedEvent, mergedData);
            }
        },
        [allTrackers, overallData, eventPrefix]
    );

    // Deliberately not prefixed or merged with `overallData`: those shape an *event*, and applying
    // them to a person would write the current page's context onto the user profile permanently.
    const identify = useCallback<IdentifyFunction>(
        (userId, traits) => {
            for (const identifier of inheritedIdentifiers) {
                identifier(userId, traits);
            }
        },
        [inheritedIdentifiers]
    );

    const context = useMemo(
        () => ({ track, trackers: inheritedTrackers, identify, identifiers: inheritedIdentifiers }),
        [track, inheritedTrackers, identify, inheritedIdentifiers]
    );
    return <AnalyticsContext.Provider value={context}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = (): AnalyticsContext => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        // Fallback so an unprovided subtree does not crash. Typed as the full context rather than
        // an object literal: a union of the two shapes would narrow `track` to Vercel's stricter
        // signature at every call site, which is not the contract this hook advertises.
        return {
            track: (event, data) => vercelTrack(event, data as Record<string, string>),
            trackers: [],
            // No provider means no identity destination — Vercel has no identify API to fall back
            // on, so this is a no-op rather than a silent half-identification.
            identify: () => {},
            identifiers: []
        };
    }
    return context;
};

export default AnalyticsProvider;
